<!-- @format -->

<template>
    <div
        class="gst-event-content d-flex flex-column flex-md-row u-width-100 u-height-100"
        :style="{ 'min-height': 0 }">
        <div
            v-if="!isEventNotAvailable && $vuetify.breakpoint.mdAndUp"
            class="col col-md-8 col-lg-9 d-flex px-0 py-0 u-height-100 u-position-relative justify-center"
            :style="{ width: '100%' }">
            <ContainerWithOverlay
                :style="{
                    maxWidth:
                        (selectedHotelReservation || highlightedHotelReservation) && !isHotelQueryId
                            ? '970px'
                            : '100%',
                }"
                :absolute="true"
                color="#FFFFFF"
                opacity="0.6"
                :overlay="showHotelFilter"
                class="gst-event-ticket-detail-content__hotel-detail u-position-relative">
                <EventHotelsMap
                    v-if="hotelsReady"
                    :event="event"
                    :is-visible="!isHotelDetailsVisible"
                    :bundle-products="bundleProductsOnlyHotels"
                    :bus-events-parent="busEvent"
                    @highlight-hotel-reservation="setHighlightedHotelReservation" />
                <div
                    v-if="
                        (selectedHotelReservation || highlightedHotelReservation) && !isHotelQueryId
                    "
                    class="gst-event-content__hotel-details u-height-100 d-flex flex-column">
                    <HotelDetailsV2
                        :has-value="!!highlightedHotelReservation || !!selectedHotelReservation"
                        :item="
                            !selectedHotelReservation
                                ? highlightedHotelReservation
                                : selectedHotelReservation.item
                        "
                        :event="event"
                        :bundle-products="bundleProductsOnlyHotels"
                        :bus-events-parent="busEvent"
                        class="flex-grow-1 u-overflow-scroll-y"
                        has-back-button
                        :back-button-text="$t('_common:buttons.backToMap')"
                        :in-location="searchedLocation"
                        @back="onHotelDetailsBackDo()" />
                </div>
            </ContainerWithOverlay>
        </div>
        <div
            v-show="loadingHotels || !isEventNotAvailable"
            class="col col-md-4 col-lg-3 pa-0 ml-auto u-height-100"
            :style="{ 'min-height': 0 }">
            <EventSidebar
                class="u-position-relative"
                :disabled-button="!canSubmit"
                :display-footer="isFooterDisplayed"
                :footer-title="$t(`theEvent._components.eventSidebar.footer.titleTicketsOnly`)"
                :submit-button-placeholder="
                    $t('theEvent._components.eventSidebar.buttons.checkout')
                "
                :bundle-products="bundleProducts"
                :event="event"
                @submit="onSubmitDo">
                <EventHotels
                    :event="event"
                    :is-no-event="isNoEvent"
                    :is-event-banner-visible="isEventBannerVisible"
                    :selected-hotel-reservation="selectedHotelReservation"
                    :bus-events-parent="busEvent"
                    :bundle-products="bundleProductsOnlyHotels"
                    :highlighted-hotel-reservation="highlightedHotelReservation"
                    @change-show-filter="onChangeShowFilterHotelDo"
                    @select-hotel-reservation="selectHotelReservation"
                    @highlight-hotel-reservation="setHighlightedHotelReservation"
                    @hotels-loaded="onHotelsLoadedDo" />
            </EventSidebar>
        </div>
        <EventNotAvailable
            v-if="isEventNotAvailable"
            class="gst-event-content__event-not-available"
            :event="event"
            :is-offer-expired="isEventOfferExpired"
            :is-offer-sold-out="isEventOfferSoldOut" />
    </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import ContainerWithOverlay from '@core/shared/components/overlays/ContainerWithOverlay.vue';
import { getQueryParamsCurrent } from '@core/utils/windowLocationUtils.js';
import hotelsConstants from '@tenant/app/utils/constants/hotels';
import tmEventConstants from '@tenant/app/utils/constants/event';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import { create as CreateBundleProductsHotelReservationModel } from '@tenant/app/modelsV2/BundleProductsHotelReservationModel';
import { create as CreateBundleProductsEventTicketModel } from '@tenant/app/modelsV2/BundleProductsEventTicketModel';
import HotelDetailsV2 from '@tenant/app/modules/hotel/components/containers/HotelDetailsV2.vue';
import prismicConstants from '@tenant/app/utils/constants/prismic';
import AllInclusivePricingMixing from './EventContent/AllInclusivePricingMixin';
import EventSidebar from './_components/EventSidebar.vue';
import EventHotels from './EventContent/EventHotels.vue';
import EventHotelsMap from './EventContent/EventHotelsMap.vue';

const CUSTOM_TYPE_HOTEL_BENEFITS_AND_DISCOUNT_MODAL =
    prismicConstants.CUSTOM_TYPES.SINGLE.HOTEL_BENEFITS_AND_DISCOUNT_MODAL;

const STORE_NAME = 'events';

export default {
    name: 'EventContent',
    components: {
        EventSidebar,
        EventHotels,
        HotelDetailsV2,
        ContainerWithOverlay,
        EventHotelsMap,
        EventNotAvailable: () => import('./EventNotAvailable'),
    },
    mixins: [EventFlowMixin, AllInclusivePricingMixing],
    props: {
        activeTab: {
            type: String,
            default: 'hotels',
        },
        event: {
            type: Object,
            required: true,
        },
        isEventBannerVisible: {
            type: Boolean,
            default: true,
        },
        busEventParent: {
            type: Object,
            default: null,
        },
        isNoEvent: {
            type: Boolean,
            default: false,
        },
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.eventV2',
    },
    data() {
        return {
            busEvent: new Vue(),
            bundleProducts: CreateBundleProductsModel(),
            selectedHotelReservation: null,
            highlightedHotelReservation: null,
            showHotelFilter: false,
            venueDetails: {
                mapUrl: '',
                mapWithlabelsUrl: '',
            },
            hotelsFailedStatus: null,
            hotelsReady: false,
            isFirstLoad: true,
            isHotelQueryId: false,
        };
    },
    computed: {
        ...mapState({
            loadingHotels: (state) => state.addHotelReservationState.hotels.loading,
            hotels: (state) => state.addHotelReservationState.hotels.list,
            productId: (state) => state.addHotelReservationState.hotels.productId,
            hotelFiltersAdultsCount: (state) =>
                state.addHotelReservationState.hotels.filters.guests.adultsCount,
            hotelFilters: (state) => state.addHotelReservationState.filters,
        }),
        ...mapGetters({
            isEventUnknown: `${STORE_NAME}/isUnknown`,
            initialLocation: 'addHotelReservationState/getLocation',
            searchedLocation: 'addHotelReservationState/hotels/getLocation',
            appLocale: 'appState/getLocale',
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
            userIsAuth: 'user/isAuth',
        }),
        isHotelDetailsVisible() {
            return !!this.highlightedHotelReservation;
        },
        hasLocationChanged() {
            return (
                this.initialLocation.longitude !== this.searchedLocation.longitude ||
                this.initialLocation.latitude !== this.searchedLocation.latitude
            );
        },
        isFooterDisplayed() {
            return !!this.selectedHotelReservation && !this.showHotelFilter;
        },
        canSubmit() {
            return !!this.selectedHotelReservation;
        },
        itemId() {
            return this.$route.params.id || this.event.id;
        },
        bundleProductsOnlyHotels() {
            return CreateBundleProductsModel(
                CreateBundleProductsEventTicketModel({ ...this.event }, null, null, null, 0, 0),
                this.selectedHotelReservation
                    ? CreateBundleProductsHotelReservationModel(this.selectedHotelReservation.item)
                    : null
            );
        },
        isEventOfferSoldOut() {
            return (
                this.hotelsReady &&
                !this.loadingHotels &&
                this.hotels.length === 0 &&
                this.isPreAllocatedFlow
            );
        },
        isEventOfferExpired() {
            return !this.loadingHotels && this.hotelsFailedStatus === 400;
        },
        isEventNotAvailable() {
            return !this.isEventUnknown && (this.isEventOfferSoldOut || this.isEventOfferExpired);
        },
        hotelsLimit() {
            return hotelsConstants.HOTEL_ONLY.limit;
        },
        isUsingLegacyRoute() {
            return this.$route.name === 'tm-eventHotels';
        },
        routeId() {
            return this.$route.params.id;
        },
    },
    watch: {
        selectedHotelReservation: function (value) {
            this.$emit('selected-hotel-reservation', value);
        },
    },
    methods: {
        ...mapActions({
            loadOne: `${STORE_NAME}/one`,
            loadHotels: `addHotelReservationState/hotels/loadPage`,
            setEventFlow: `${STORE_NAME}/setEventFlow`,
        }),
        onSubmitDo() {
            if (!this.userIsAuth && this.redeemPointsToggle) {
                const translationPath = 'theEventPostPurchase.loginBeforeReserveModal';
                const componentProps = {
                    title: this.$t(`${translationPath}.title`),
                    content: `<div class="gst-login-before-reserve-modal">
                        <p class="gst-login-before-reserve-modal__subtitle mb-2">${this.$t(`${translationPath}.subtitle`)}</p>
                        <p class="mb-n2">${this.$t(`${translationPath}.description`)}</p>
                    </div>`,
                    confirmButton: this.$t(`${translationPath}.buttons.login`),
                    cancelButton: this.$t(`${translationPath}.buttons.continueAsGuest`),
                    showCloseIcon: true,
                    onConfirmFn: () => {
                        this.$router.push({ name: 'auth.login', params: { method: 'redirect' } });
                    },
                    onCancelFn: () => {
                        this.onGoToCheckoutDo();
                    },
                    onCloseFn: () => {},
                };

                this.$modal.show(
                    () => import('@tenant/app/components/modals/ConfirmationModal'),
                    {
                        ...componentProps,
                    },
                    {
                        'max-width': 858,
                        persistent: true,
                    }
                );
            } else this.onGoToCheckoutDo();
        },
        selectHotelReservation(hotelReservation) {
            this.selectedHotelReservation = hotelReservation;

            this.bundleProducts = CreateBundleProductsModel(
                null,
                this.selectedHotelReservation
                    ? CreateBundleProductsHotelReservationModel(hotelReservation.item)
                    : null
            );
        },
        setHighlightedHotelReservation(hotelReservation) {
            if (!this.isFirstLoad) {
                this.isHotelQueryId = false;
            }
            this.highlightedHotelReservation = hotelReservation;
        },
        onGoToCheckoutDo() {
            const { selectedHotelReservation } = this;
            const room = selectedHotelReservation.item.selectedRoom;
            const roomOffer = room.selectedRate.offer;
            let payload = {};

            if (selectedHotelReservation) {
                payload = {
                    hotelReservation: {
                        hotel: selectedHotelReservation.item,
                        room: selectedHotelReservation.item.selectedRoom,
                        accommodation: {
                            startDateTime: selectedHotelReservation.startDateTime,
                            endDateTime: selectedHotelReservation.endDateTime,
                            roomsCount: selectedHotelReservation.roomsCount,
                            guests: selectedHotelReservation.guests,
                        },
                        originatingEventId: this.productId,
                    },
                };

                if (this.isUsingLegacyRoute) {
                    payload.hotelReservation.originatingExternalEventId = this.routeId;
                }

                if (roomOffer) {
                    payload.offer = {
                        id: roomOffer.id,
                    };
                }

                if (this.hasLocationChanged || this.isEventUnknown) {
                    payload.searchedLocation = { ...this.searchedLocation };
                }
            }

            this.$emit('checkout', payload);
        },
        onChangeSelectedHotelDo() {
            this.selectHotelReservation(null);
        },
        onRemoveSelectedHotelDo() {
            this.selectHotelReservation(null);
        },
        onChangeShowFilterHotelDo(value) {
            this.showHotelFilter = value;
        },
        onSelectRoomDo(hotelReservation, filters) {
            this.selectedHotelReservation = {
                item: hotelReservation,
                ...filters,
            };

            this.bundleProducts.setHotelReservation({
                ...this.bundleProducts.hotelReservation,
                hotel: hotelReservation,
            });
            this.bundleProducts.hotelReservation.setSelectedRoom(hotelReservation.selectedRoom);
        },
        onEventHotelRoomRateChangedBackDo() {
            this.selectHotelReservation(null);

            this.loadHotels({
                refresh: true,
                filters: this.hotelFilters,
                limit: this.hotelsLimit,
            });
        },
        onHotelDetailsBackDo() {
            this.setHighlightedHotelReservation(null);
            this.selectHotelReservation(null);
        },
        onHotelsLoadedDo(response) {
            this.hotelsFailedStatus = response.success ? null : response.status;
            this.hotelsReady = true;
            // check if hotels have offers and if not switch to Open inventory flow
            if (!this.hotelsFailedStatus && this.productId && this.hotels.length) {
                // hotels contains only hotels with guest rooms and room rates
                const hasRoomRateOffer = !!this.hotels[0].rooms[0].rates[0].offer?.id;
                if (!hasRoomRateOffer && this.isFirstLoad) {
                    // switch from pre-allocated to open inventory
                    this.setEventFlow({ flow: tmEventConstants.FLOWS.HOTELS_OPEN_INVENTORY });
                    //this.openOffersModal();
                }
            }
            if (this.isOpenInventoryFlow && this.isFirstLoad && this.hotels.length) {
                this._showHotelsAllInclusivePricingInfo(this.hotels);
                this.isFirstLoad = false;
            }
        },
        // The hotel offers modal is currently disabled due to updated business requirements (TMH-3268).
        // Do not remove the implementation as it might be re-enabled in the future.
        async openOffersModal() {
            let modalContent = await this.$prismic.client.getSingle(
                CUSTOM_TYPE_HOTEL_BENEFITS_AND_DISCOUNT_MODAL,
                { lang: this.appLocale }
            );
            if (!modalContent) {
                modalContent = await this.$prismic.client.getSingle(
                    CUSTOM_TYPE_HOTEL_BENEFITS_AND_DISCOUNT_MODAL
                );
            }
            if (modalContent) {
                const title = modalContent.data.title;
                const content = modalContent.data.content;
                const buttonLabel = modalContent.data.button_label;
                this.$modal.show(
                    () => import('@tenant/app/components/modals/HotelOffersModal'),
                    {
                        title,
                        content: content,
                        buttonLabel: buttonLabel,
                    },
                    {
                        width: 'unset',
                    }
                );
            }
        },
    },
    created() {
        this.busEvent.$on('change-selected-hotel', this.onChangeSelectedHotelDo);
        this.busEvent.$on('remove-selected-hotel', this.onRemoveSelectedHotelDo);
        this.busEvent.$on('add-item', this.onSelectRoomDo);
        this.busEventParent.$on(
            'event-hotel-room-rate-changed-back',
            this.onEventHotelRoomRateChangedBackDo
        );
        this.isHotelQueryId = !!getQueryParamsCurrent().hotelId;
    },
    destroyed() {
        this.busEvent.$off('change-selected-hotel');
        this.busEvent.$off('remove-selected-hotel');
        this.busEvent.$off('add-item');
        this.busEventParent.$off('event-hotel-room-rate-changed-back');
        this._listenerBrowserBack && this._listenerBrowserBack();

        this._destroyed = true;
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-login-before-reserve-modal {
    .gst-login-before-reserve-modal__subtitle {
        font-size: font-size('xxl');
        font-weight: font-weight('medium');
    }
}

.gst-event-ticket-detail-content__hotel-detail {
    .v-overlay {
        z-index: $z-index-event-page-over-layer + 1 !important;
    }
}

.gst-event-content__hotel-details {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: $z-index-map-hotel-details;
}

.gst-event-content__event-not-available {
    position: absolute;
    width: 100%;
}

@include mobile-only {
    .gst-login-before-reserve-modal__subtitle {
        font-size: font-size('l');
        font-weight: font-weight('medium');
    }
}
</style>
